import {
  Button,
  Modal,
  SelectOptionType,
  TextInput,
  addToast,
} from '@octano/global-ui';
import { ReactElement, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import RemoteSearchSelect from '../../../../../components/form/RemoteSearchSelect';
import { TranslationsKeys } from '../../../../../locales/translations';
import { searchCourseGroupingsForCourseAction } from '../../actions';
import { CourseGroupingPick } from '../../types';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  excludeIds: number[];
  onConfirm: (g: CourseGroupingPick) => void;
};

interface GroupingOption extends SelectOptionType {
  raw: CourseGroupingPick;
}

/**
 * HU 15577 — Task B4: Modal selector de Agrupación de asignaturas.
 *
 * Permite al administrador buscar una Agrupación vía RemoteSearchSelect y
 * confirmar su asociación al curso en edición.
 * - Filtra en cliente los ids ya presentes en el formulario (excludeIds).
 * - Muestra Código y Escuela como campos de solo lectura tras seleccionar.
 * - Sin react-hook-form: estado local únicamente.
 */
export default function AddCourseGroupingModal({
  isOpen,
  onClose,
  excludeIds,
  onConfirm,
}: Props): ReactElement {
  const { t } = useTranslation(TranslationsKeys.ACADEMIC_OFFER_COURSES_UPDATE);
  const [selected, setSelected] = useState<CourseGroupingPick | null>(null);
  const lastRequestIdRef = useRef<number>(0);

  const filterOptions = useCallback(
    async (searchText: string): Promise<GroupingOption[]> => {
      const requestId = ++lastRequestIdRef.current;
      try {
        const result = await searchCourseGroupingsForCourseAction({
          name: searchText,
          page: 0,
          items_per_page: 20,
        });
        // Ignore stale responses
        if (requestId !== lastRequestIdRef.current) {
          return [];
        }
        if (!result.success) {
          addToast({
            icon: 'error',
            color: 'danger',
            text: t('groupings.errors.notFound'),
          });
          return [];
        }
        const items = result.data?.items ?? [];
        return items
          .filter((item) => !excludeIds.includes(item.id))
          .map((item) => ({
            value: item.id,
            label: `${item.name} (${item.code})`,
            raw: {
              id: item.id,
              name: item.name,
              code: item.code,
              school: item.school,
            },
          }));
      } catch {
        if (requestId === lastRequestIdRef.current) {
          addToast({
            icon: 'error',
            color: 'danger',
            text: t('groupings.errors.notFound'),
          });
        }
        return [];
      }
    },
    [excludeIds, t],
  );

  const handleChange = useCallback((option: GroupingOption | null) => {
    if (option) {
      setSelected(option.raw);
    } else {
      setSelected(null);
    }
  }, []);

  const handleClose = useCallback(() => {
    setSelected(null);
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    if (!selected) {
      return;
    }
    onConfirm(selected);
    setSelected(null);
    onClose();
  }, [selected, onConfirm, onClose]);

  if (!isOpen) {
    return <></>;
  }

  return (
    <Modal isOpen={true} toggle={handleClose} closeOnBackdrop={false}>
      <div>
        <h1 className="text-dark text-center fw-600 fs-20">
          {t('groupings.modal.title')}
        </h1>
        <Row>
          <Col xs={12}>
            <RemoteSearchSelect
              name="courseGroupingSearch"
              label={t('groupings.modal.fields.grouping')}
              filterOptions={filterOptions}
              onChange={(option) =>
                handleChange(option as GroupingOption | null)
              }
              isClearable
            />
          </Col>
          <Col xs={12}>
            <TextInput
              name="courseGroupingCode"
              label={t('groupings.modal.fields.code')}
              value={selected?.code ?? ''}
              disabled
            />
          </Col>
          <Col xs={12}>
            <TextInput
              name="courseGroupingSchool"
              label={t('groupings.modal.fields.school')}
              value={selected?.school?.name ?? ''}
              disabled
            />
          </Col>
        </Row>
        <Row className="pt-4">
          <Col xs={12} lg={6} className="pb-2 order-2 order-lg-1">
            <Button
              type="button"
              outlined
              onClick={handleClose}
              text={t('groupings.modal.cancel')}
              fullwidth
            />
          </Col>
          <Col xs={12} lg={6} className="pb-2 order-1 order-lg-2">
            <Button
              type="button"
              text={t('groupings.modal.submit')}
              onClick={handleConfirm}
              disabled={selected == null}
              fullwidth
            />
          </Col>
        </Row>
      </div>
    </Modal>
  );
}
